/** @jsx isolateComponent */

import React, { SyntheticEvent } from 'react';
import Imgix from 'react-imgix';
import LazyLoad from 'react-lazyload';
import {
  Animation,
  ShadowName,
  Shadows,
} from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

export type ImgProps = {
  /** Specifies alternative text for the image */
  alt: string;

  /** Inform screen-readers that they should ignore the image */
  ariaHidden?: boolean;

  /**
   * Only supported by Imgix URLs. Provide an aspect ratio as a string, in the
   * form of `width`:`height` e.g. "16:9". Imgix crop the image to fulfill the
   * desired aspect ratio.
   */
  aspectRatio?: string;

  /**
   * The maximum height of the image in pixels. If both `height` and `width`
   * are set, and the image is an Imgix URL, the image will be center-cropped
   * by the Imgix service.
   */
  height?: number;

  /**
   * The offset in pixels at which you wish the image lazyloader to trigger.
   * For example, if this is set to `100`, it will load the image when it is
   * 100px away from appearing at the the bottom of the viewport.
   * Also accepts negative numbers, or an array of numbers which will trigger
   * both the top-edge and bottom-edge offset.
   * If you provide a value of `null` value, you will opt-out of lazy loading.
   */
  lazyloadOffset?: number | number[] | null;

  /**
   * Will properly load content that is initially overflowed outside of parent
   * container (see caveats in docs: https://www.npmjs.com/package/react-lazyload#overflow)
   */
  lazyloadOverflow?: boolean;

  /** Enables 300ms throttling of the lazy-loading mechanism. */
  lazyloadThrottle?: boolean;

  /** Toggles display of a border radius on the image. */
  hasRadius?: boolean;

  /**
   * The CSS object-fit property, which is necessary to tweak image sizing in
   * some situations. This is useful to leverage imgix-powered performant
   * images in situations where a background image would otherwise be required.
   */
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';

  /**
   * Called when image fails to load
   */
  onError?: (a: SyntheticEvent<HTMLImageElement>) => void;

  /**
   * Called when image loads
   */
  onLoad?: (a: SyntheticEvent<HTMLImageElement>) => void;

  /** Applies a `box-shadow` with the specified shadow. */
  shadow?: ShadowName;

  /**
   * A comma separated list of source sizes. Each source size contains a media
   * query condition and the source size value. With this information, the
   * client will select one of the image sources available from the `srcset`
   * attribute.
   * The Img component uses `react-imgix` to automatically generate the `srcset`
   * list. The `srcset` list contains a comma separated list indicating possible
   * image sources the client can use.
   */
  sizes?: string;

  /** Source URL of the image */
  src: string;

  /**
   * The maximum width of the image in pixels. If both `height` and `width`
   * are set, and the image is an Imgix URL, the image will be center-cropped
   * by the Imgix service.
   */
  width?: number | null;
};

const Styles = labelStyles(__filename, {
  root: (props: ImgProps) => [
    {
      borderRadius: 0,
      transitionProperty: 'box-shadow',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
      // `width: 100%` is being added because the overwhelmingly common use
      // case for the Img component is to require it to fill its parent
      // container. Without this, consumers of Img are unable to force it to be
      // full width without stretching its aspect ratio. The inverse remains
      // possible: specifying a width, or wrapping the image in a container of
      // fixed width, can be used to limit the image size.
      width: '100%',
    },
    props.hasRadius &&
      withModifier('hasRadius', {
        borderRadius: 10,
      }),
    props.shadow &&
      withModifier('hasShadow', {
        boxShadow: Shadows[props.shadow],
      }),
    props.objectFit &&
      withModifier('objectFit', {
        objectFit: props.objectFit,
      }),
    props.width &&
      withModifier('width', {
        width: props.width,
      }),
  ],
});

/**
 * Renders an `<img>` tag.
 *
 * Exposes additional features like lazy-loading, server-side cropping and
 * resizing, and responsive image support.
 *
 * Tags: core-ui
 */
const Img: React.FC<ImgProps> = props => {
  const {
    alt,
    ariaHidden,
    aspectRatio,
    height,
    lazyloadOffset,
    lazyloadOverflow,
    lazyloadThrottle,
    onError,
    onLoad,
    sizes,
    src,
    width,
  } = props;

  const imageNode = (
    <Imgix
      css={Styles.root(props)}
      data-testid="img"
      height={height}
      htmlAttributes={{
        alt,
        'aria-hidden': ariaHidden,
        onError,
        onLoad,
      }}
      imgixParams={{
        ar: aspectRatio,
      }}
      sizes={sizes}
      src={src}
      width={width}
    />
  );
  return lazyloadOffset === null ? (
    imageNode
  ) : (
    <LazyLoad
      height={height}
      offset={lazyloadOffset}
      once={true}
      overflow={lazyloadOverflow}
      throttle={lazyloadThrottle}
    >
      {imageNode}
    </LazyLoad>
  );
};

Img.defaultProps = {
  hasRadius: true,
  lazyloadOffset: 200,
  lazyloadThrottle: false,
};
export { Img };
